import axios from 'axios';
import { dispatchAuthSessionCleared } from '@/constants/authEvents';

const ACCESS_TOKEN_KEY = 'cardio-ins::access-token';
const REFRESH_TOKEN_KEY = 'cardio-ins::refresh-token';
const SESSION_EXP_KEY = 'cardio-ins::expires-at';
const LOGIN_PATH = '/login';
const PORTAL_LOGIN_PATH_KEY = 'cardio-ins::portal-login-path';
const REFRESH_ENDPOINT = '/auth/refresh';
const AUTH_FREE_PATHS = ['/auth/login', '/auth/register', '/auth/refresh', '/portal/branding'];

/** Recuerda la URL de login del portal para redirigir allí al expirar la sesión */
export const rememberPortalLoginPath = (path: string) =>
	localStorage.setItem(PORTAL_LOGIN_PATH_KEY, path);

/** Olvida la URL de login del portal (login corporativo estándar) */
export const forgetPortalLoginPath = () => localStorage.removeItem(PORTAL_LOGIN_PATH_KEY);

const getLoginRedirectPath = () => localStorage.getItem(PORTAL_LOGIN_PATH_KEY) ?? LOGIN_PATH;

const clearSession = () => {
	[ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, SESSION_EXP_KEY].forEach(key =>
		sessionStorage.removeItem(key),
	);
	dispatchAuthSessionCleared();
};

const clearSessionAndRedirect = () => {
	clearSession();
	const loginPath = getLoginRedirectPath();
	if (window.location.pathname !== loginPath) {
		window.location.replace(loginPath);
	}
};

const getStoredSession = () => {
	const accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
	const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
	const expiresAt = sessionStorage.getItem(SESSION_EXP_KEY);

	return {
		accessToken,
		refreshToken,
		expiresAt: expiresAt ? Number(expiresAt) : null,
	};
};

const persistSession = (payload: {
	accessToken: string;
	refreshToken: string;
	expiresIn: number;
}) => {
	sessionStorage.setItem(ACCESS_TOKEN_KEY, payload.accessToken);
	sessionStorage.setItem(REFRESH_TOKEN_KEY, payload.refreshToken);
	sessionStorage.setItem(SESSION_EXP_KEY, String(Date.now() + payload.expiresIn * 1000));
};

const baseURL = import.meta.env.VITE_API_URL;

export const axiosApi = axios.create({
	baseURL,
	responseType: 'json',
	timeout: 100000, // 10 segundos
	timeoutErrorMessage: 'La petición tardó demasiado en completarse',
});

const refreshHttpClient = axios.create({
	baseURL,
});

let refreshPromise: Promise<string | null> | null = null;

const refreshAccessToken = async (): Promise<string | null> => {
	if (refreshPromise) {
		return refreshPromise;
	}

	const { refreshToken } = getStoredSession();
	if (!refreshToken) return null;

	refreshPromise = refreshHttpClient
		.post(REFRESH_ENDPOINT, { refreshToken })
		.then(response => {
			const { accessToken, refreshToken: newRefreshToken, expiresIn } = response.data;
			if (!accessToken || !newRefreshToken || !expiresIn) {
				throw new Error('Respuesta de refresh inválida');
			}
			persistSession({
				accessToken,
				refreshToken: newRefreshToken,
				expiresIn,
			});
			return accessToken;
		})
		.catch(error => {
			clearSessionAndRedirect();
			return Promise.reject(error);
		})
		.finally(() => {
			refreshPromise = null;
		});

	return refreshPromise;
};

const ensureValidAccessToken = async (requestUrl?: string): Promise<string | null> => {
	if (requestUrl && AUTH_FREE_PATHS.some(path => requestUrl.includes(path))) {
		return null;
	}

	const { accessToken, expiresAt } = getStoredSession();

	if (accessToken && expiresAt && expiresAt > Date.now()) {
		return accessToken;
	}

	return refreshAccessToken();
};

// Interceptor para manejar errores de red y sesión
axiosApi.interceptors.response.use(
	response => response,
	error => {
		if (error.response?.status === 401) {
			const requestUrl: string = error.config?.url ?? '';
			const isAuthFree = AUTH_FREE_PATHS.some(path => requestUrl.includes(path));
			const isOnLoginPage = window.location.pathname.startsWith(LOGIN_PATH);

			// No expulsar al usuario si el 401 viene del propio login
			// o si ya está en una pantalla de login (incluye /login/:slug)
			if (!isAuthFree && !isOnLoginPage) {
				clearSessionAndRedirect();
			}
		} else {
			if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
				console.warn('Error de red o timeout:', error.message);
			}

			// Manejar específicamente errores de stream para imágenes
			if (error.message?.includes('ERR_STREAM_PREMATURE_CLOSE')) {
				console.warn(
					'Error de stream prematuro - posible cancelación de petición: ',
					error.message,
				);
			}
		}

		return Promise.reject(error);
	},
);

// Interceptor para agregar token y headers específicos
axiosApi.interceptors.request.use(
	async config => {
		const token = await ensureValidAccessToken(config.url);

		if (token) {
			config.headers = config.headers ?? {};
			config.headers.Authorization = `Bearer ${token}`;
		}

		// Si es una petición para avatar, agregar headers de cache
		if (config.url?.includes('/avatar')) {
			config.headers = config.headers ?? {};
			config.headers['Cache-Control'] = 'public, max-age=3600'; // 1 hora
		}
		return config;
	},
	error => {
		return Promise.reject(error);
	},
);
